import React, { Component } from "react";
import CustomTable from "../2/Controls/CustomTable";
import ViewPage from "../2/Controls/ViewPage";
import { exportFile } from "../../actions/DataExporter";
import SideBySideLayout from "../2/Controls/SideBySideLayout";

class FeedbackLinkDetail extends Component {

  constructor(props) {
    super(props);
    let feedbackLinkData = {};
    if (this.props.location && this.props.location.state && this.props.location.state.data) {
      feedbackLinkData = this.props.location.state.data;
    }

    this.state = {
      list: [],
      totalCount: 0,
      loading: false,
      page: 1,
      pageSize: 25,
      listData: [],
      feedbackLinkData: feedbackLinkData,
      questions: [], // Store questions for column headers
      columns: [], // Will be dynamically set
      searchText: ""
    };
  }

  componentDidMount() {
    this.loadData();
  }

  componentDidUpdate(prevProps, prevState) {
    if (
      prevState.page !== this.state.page ||
      prevState.pageSize !== this.state.pageSize
    ) {
      this.loadData();
    }
  }

  // ======================
  // LOAD DATA
  // ======================

  loadData = async () => {
    this.setState({ loading: true });
    try {
      const feedbackLinkId = this.state.feedbackLinkData.key;

      if (!feedbackLinkId) {
        console.warn("No feedback link ID found");
        this.setState({
          loading: false,
          list: [],
          totalCount: 0
        });
        return;
      }

      const result = await this.props.getFeedbackResponsesByLinkId(feedbackLinkId);

      if (result && result.hasOwnProperty('code') && result.code === "0") {
        // Extract questions from the response
        const questions = result.hasOwnProperty('questions') ? result.questions : [];

        // Build dynamic columns based on questions
        const columns = this.buildColumns(questions);

        // Transform responses into rows
        const data = result.hasOwnProperty('data') ? result.data : [];
        const transformedData = this.transformResponsesToRows(questions, data);

        this.setState({
          questions: questions,
          list: transformedData,
          totalCount: transformedData.length,
          loading: false,
          columns: columns
        });
      } else {
        if (result && result.hasOwnProperty('message')) {
          console.error(result.message);
        } else {
          console.error("Failed to fetch feedback");
        }
        this.setState({
          loading: false,
          list: [],
          totalCount: 0
        });
      }
    } catch (error) {
      console.error("Error loading feedback:", error);
      this.setState({
        loading: false,
        list: [],
        totalCount: 0
      });
    }
  };

  handleExport = async () => {
    // Check if there's data to export
    if (!this.state.list || this.state.list.length === 0) {
      console.warn("No data to export");
      return;
    }

    // Prepare data for export
    const dataToExport = this.state.list.map(row => {
      // Remove internal fields if needed
      const { _originalResponse, ...exportRow } = row;
      return exportRow;
    });

    // Build column configuration for export
    const columns = this.state.columns.map(col => {
      return {
        header: col.Name,
        field: col.Columns[0].Column, // Get the field name
        type: col.Type === "field" ? "string" : "string" // Map type as needed
      };
    });

    // Build export config
    const exportConfig = {
      data: dataToExport,
      columns: columns,
      fileType: "CSV", // Default to CSV, can be "EXCEL" or "PDF"
      title: this.state.feedbackLinkData.Link || "Feedback_Export",
      landscape: false,
      allowEmpty: false
    };

    // Call the export function
    await exportFile(exportConfig);
  };

  // ======================
  // BUILD DYNAMIC COLUMNS
  // ======================

  buildColumns = (questions) => {
    // Always include a "Respondent" column if you want to identify responses
    const columns = [];

    // Add a column for each question
    if (questions && Array.isArray(questions)) {
      questions.forEach((question, index) => {
        columns.push({
          Name: question.hasOwnProperty('Question') ? question.Question : `Question ${index + 1}`,
          Type: "field",
          Columns: [{ Column: `q_${question.hasOwnProperty('id') ? question.id : index}` }],
          width: "200px"
        });
      });
    }

    // Optionally add a timestamp column
    columns.push({
      Name: "Submitted On",
      Type: "field",
      Columns: [{ Column: "submittedOn" }],
      width: "180px"
    });

    return columns;
  };

  // ======================
  // TRANSFORM RESPONSES TO ROWS
  // ======================

  transformResponsesToRows = (questions, responses) => {
    if (!responses || !Array.isArray(responses)) {
      return [];
    }

    return responses.map((response, index) => {
      // Create a row object
      const row = {
        respondent: `Response ${index + 1}`,
        submittedOn: (response && response.hasOwnProperty('ActionOn'))
          ? new Date(response.ActionOn).toLocaleString()
          : "N/A",
        // Store the full response for potential drill-down
        _originalResponse: response
      };

      // Create a map of QuestionID to Answer for quick lookup
      const answerMap = {};
      if (response && response.hasOwnProperty('Answers') && Array.isArray(response.Answers)) {
        response.Answers.forEach(answer => {
          if (answer && answer.hasOwnProperty('QuestionID')) {
            answerMap[answer.QuestionID] = (answer.hasOwnProperty('Answer')) ? answer.Answer : "-";
          }
        });
      }

      // Map each question to its answer
      if (questions && Array.isArray(questions)) {
        questions.forEach(question => {
          if (question && question.hasOwnProperty('id')) {
            const key = `q_${question.id}`;
            row[key] = answerMap.hasOwnProperty(question.id) ? answerMap[question.id] : "-";
          }
        });
      }

      return row;
    });
  };

  // ======================
  // SEARCH HANDLER
  // ======================

  onSearch = (searchText) => {
    console.log("Search:", searchText);
    // Implement search logic on the transformed data
    if (searchText && searchText.trim()) {
      const searchLower = searchText.toLowerCase().trim();
      const filteredList = this.state.list.filter(row => {
        // Search through all fields except _originalResponse
        return Object.keys(row).some(key => {
          if (key === '_originalResponse') {
            return false;
          }
          if (key === 'respondent' || key === 'submittedOn') {
            return String(row[key]).toLowerCase().includes(searchLower);
          }
          // Search through question answers
          return String(row[key]).toLowerCase().includes(searchLower);
        });
      });

      this.setState({
        list: filteredList,
        totalCount: filteredList.length,
        page: 1, // Reset to first page
        searchText: searchText
      });
    } else {
      // If search is cleared, reload original data
      this.setState({ searchText: searchText }, () => {
        this.loadData();
      });
    }
  };

  // ======================
  // PAGINATION
  // ======================

  onNextClick = () => {
    const totalPages = Math.ceil(this.state.totalCount / this.state.pageSize);
    if (this.state.page < totalPages) {
      this.setState({ page: this.state.page + 1 });
    }
  };

  onPreviousClick = () => {
    if (this.state.page > 1) {
      this.setState({ page: this.state.page - 1 });
    }
  };

  onFirstClick = () => this.setState({ page: 1 });

  onLastClick = () => {
    const totalPages = Math.ceil(this.state.totalCount / this.state.pageSize);
    this.setState({ page: totalPages });
  };

  onRecordsPerPageCountChange = (size) => {
    this.setState({
      pageSize: Number(size),
      page: 1
    });
  };

  // ======================
  // HEADER BUTTONS
  // ======================

  onHeaderButtonClicked = (btn) => {
    if (btn.Action === "refresh") {
      this.onRefreshClick();
    }
    if (btn.Action === "export") {
      this.handleExport();
    }
  };

  onRefreshClick = () => {
    this.loadData();
  };

  onPageChange = (page, pageSize) => {
    this.setState(
      { page, pageSize },
      () => {
        this.loadData();
      }
    );
  };

  // ======================
  // RENDER
  // ======================

  render() {
    const startIndex = (this.state.page - 1) * this.state.pageSize;
    const endIndex = startIndex + this.state.pageSize;
    const paginatedList = this.state.list.slice(startIndex, endIndex);

    // Convert columns to CustomTable format
    const tableColumns = this.state.columns.map(col => ({
      headerName: col.Name,
      field: col.Columns && col.Columns.length > 0 ? col.Columns[0].Column : "",
      type: col.Type === "field" ? "string" : "string",
      width: col.width || "150px"
    }));

    // Define icon buttons for ViewPage
    const iconButtons = [
      {
        icon: "refresh",
        onClick: this.onRefreshClick,
        label: "Refresh"
      },
      {
        icon: "download",
        onClick: this.handleExport,
        label: "Export"
      }
    ];

    return (
      <div className="header-margin">
      <SideBySideLayout
      showFullScreen={true}
      right={
      <ViewPage
        name={this.state.feedbackLinkData.FormTitle }
        description={this.state.feedbackLinkData.Link}
        iconButtons={iconButtons}
        handleRemoveBtnClick={() => {
          this.props.history.push({
            pathname: "/home/feedbacks"
          });
        }}
      >
          
            <CustomTable
              columns={tableColumns}
              rows={paginatedList}
              loading={this.state.loading}
              loadingRows={5}
              noShadow={false}
              allowCompressInMobile={true}
              pagination={{
                currentPage: this.state.page,
                totalCount: this.state.totalCount,
                rowsPerPage: this.state.pageSize,
                onPageChange: this.onPageChange,
                loading: this.state.loading
              }}
              showTextOnRows={!this.state.loading && paginatedList.length === 0 ? "No feedback responses found" : undefined}
            />
      </ViewPage>
      }/>
      </div>
    );
  }
}

export default FeedbackLinkDetail;